import { RBWdrafts } from './ReplyBoxWrapperDrafts';
import CONSTS from 'constants/index.js';
import CONSTANTS from 'chat-constants';
import { v4 as uuid } from 'uuid';

export const createMessage = (props, message) => {
  const msg = {
    conversation_id: props.conversationId,
    sender_channel_id: message.noteMode
      ? props.noteChannel.id
      : props.geckoChannel.id,
    sender_id: Number(props.agent.id),
    sender: props.agent,
    recipient_id: props.contact.id,
    recipient_channel_id: message.recipient_channel_id,
    metadata: {
      ...message.metadata,
      subject: getSubject(props.messages),
      web_id: uuid(),
    },
    content: message.content,
    editor_object: message.editor_object,
    attachments: message.attachments,
  };
  let reduced = {};
  Object.keys(msg).forEach((key) => {
    if (msg[key]) {
      reduced[key] = msg[key];
    }
  });
  return reduced;
};

export const createPath = (props, message) => {
  return Number(props.noteChannel.id) === Number(message.recipient_channel_id)
    ? 'internal-note'
    : 'send-message';
};

export const createTempMessage = (props, message) => {
  const temp = Object.assign({}, message, {
    status: 'sending',
    received_at: new Date().toString(),
    sender_channel: findChannel(props.channels, message.sender_channel_id),
    recipient_channel: findChannel(
      props.channels,
      message.recipient_channel_id
    ),
    attachments: message.attachments,
    metadata: {
      web_id: message?.metadata?.web_id,
    },
  });
  return temp;
};

export const findChannel = (channels, id) =>
  channels.find((c) => Number(c.id) === Number(id));

export const getAllMessageChannelIds = (messages) => {
  const nested = messages.map((msg) =>
    [Number(msg.sender_channel.id), Number(msg.recipient_channel_id)].filter(
      (id) => id
    )
  );
  let flattened = [].concat.apply([], nested);
  // reverse the flattened array so the unique ids kept are the ones from the most recent messages
  flattened.reverse();
  let uniques = flattened.filter((id, i) => flattened.indexOf(id) === i);
  // reverse again so we're back in chronological order
  uniques.reverse();
  return uniques;
};

export const getAvailableChannels = (props) => {
  let ids = getSendableMessageChannelIds(
    props.messages,
    props.noteChannel,
    props.geckoChannel
  );
  if (props.contact.tel && props.contact.email) {
    ids = getComposeableChannelIds(props.channels).concat(ids);
    ids = ids.reverse();
    ids = ids.filter((id, i) => ids.indexOf(id) === i);
  } else if (props.contact.email) {
    ids = getEmailChannels(props.channels).concat(ids);
    ids = ids.reverse();
    ids = ids.filter((id, i) => ids.indexOf(id) === i);
  } else if (props.contact.tel) {
    ids = getSmsChannels(props.channels).concat(ids);
    ids = ids.reverse();
    ids = ids.filter((id, i) => ids.indexOf(id) === i);
  }
  let availableChannels = ids
    .map((id) => props.channels.find((chan) => Number(chan.id) === id))
    .filter(
      (channel) =>
        channel &&
        !channel.deletedAt &&
        !CONSTANTS.CHANNEL.UNPICKABLE_TYPES.includes(channel.type)
    );
  return availableChannels.concat([props.noteChannel]);
};

export const getComposeableChannelIds = (channels) => {
  return channels
    .filter((chan) => CONSTANTS.CHANNEL.COMPOSABLE_TYPES.includes(chan.type))
    .map((chan) => Number(chan.id));
};

export const getEditorMode = (channelType) => {
  return CONSTS.CHANNEL_CONTENT_TYPES[channelType];
};

export const getEmailChannels = (channels) => {
  return channels
    .filter((chan) => chan.type === CONSTANTS.CHANNEL.TYPE_MAILGUN)
    .map((chan) => Number(chan.id));
};

export const getSendableMessageChannelIds = (
  messages,
  noteChannel,
  geckoChannel
) => {
  const messageChanIds = getAllMessageChannelIds(messages);
  const internal = [Number(noteChannel.id), Number(geckoChannel.id)];
  return messageChanIds.filter((id) => internal.indexOf(id) < 0);
};

export const getSendableMessageChannels = (props) => {
  const ids = getSendableMessageChannelIds(
    props.messages,
    props.noteChannel,
    props.geckoChannel
  );
  return ids.map((id) => props.channels.find((chan) => Number(chan.id) === id));
};

export const getSmsChannels = (channels) => {
  return channels
    .filter((chan) => chan.type === 'twilio')
    .map((chan) => Number(chan.id));
};

export const getSubject = (messages) => {
  const metadata = messages.map((msg) => msg.metadata).filter((data) => data);
  const subjects =
    metadata.length > 0
      ? metadata.map((data) => data.subject).filter((subj) => subj)
      : [];
  return subjects.length > 0 ? `RE: ${subjects[0]}` : null;
};

export const shouldAssign = (props, message) => {
  return (
    !(message.recipient_channel_id === props.noteChannel.id) &&
    props.currentFilter === CONSTS.FILTER_UNASSIGNED &&
    !props.conversationAgentIds.find((a) => a === props.contact.id)
  );
};

export const getRecipientChannel = (
  availableChannels,
  lastMessageChannelId,
  contact,
  allChannels
) => {
  const foundChannel = allChannels.find(
    (channel) => Number(channel.id) === Number(lastMessageChannelId)
  );
  if (!foundChannel) { return null };
  if (foundChannel.type === CONSTANTS.CHANNEL.TYPE_FORM_RESPONSE) {
    return null;
  }

  const lastMessageChannel = availableChannels.find(
    (channel) => Number(channel.id) === Number(lastMessageChannelId)
  );
  if (lastMessageChannel) {
    return lastMessageChannel;
  }
  const emailChannel = availableChannels.find(
    (channel) => channel.type === CONSTANTS.CHANNEL.TYPE_MAILGUN
  );
  if (contact.email && emailChannel) {
    return emailChannel;
  }
  const smsChannel = availableChannels.find(
    (channel) => channel.type === CONSTANTS.CHANNEL.TYPE_TWILIO
  );
  if (contact.tel && smsChannel) {
    return smsChannel;
  }
  console.error('No suitable channel found for Contact');
  return availableChannels[0];
};

export const getDraftChannel = (draft, availableChannels) => {
  if (!draft.channel) {
    return null;
  }
  // If the draft channel is a notes channel but it isn't in note mode then unselect the channel
  if (
    draft.channel.type === CONSTANTS.CHANNEL.TYPE_INTERNAL_NOTE &&
    !draft.noteMode
  ) {
    return null;
  }
  return availableChannels.find(
    (channel) => Number(channel.id) === Number(draft.channel)
  );
};

export const state = (props) => {
  const availableChannels = getAvailableChannels(props);
  const draft = RBWdrafts.retrieveDrafts(props);
  const draftChannel = getDraftChannel(draft, availableChannels);
  const recipientChannel =
    draftChannel ||
    getRecipientChannel(
      availableChannels,
      props.lastMessageChannel,
      props.contact,
      props.channels
    );

  return {
    availableChannels: availableChannels,
    editorMode: recipientChannel
      ? getEditorMode(recipientChannel.type)
      : 'plain',
    editorState: draft.message ? draft.message : draft.note,
    noteMode: draft.noteMode ? { on: true } : { on: false },
    recipientChannel: recipientChannel,
    suggestions: [],
  };
};

export const RBWfuncs = {
  createMessage,
  createPath,
  createTempMessage,
  findChannel,
  getAllMessageChannelIds,
  getAvailableChannels,
  getRecipientChannel,
  getDraftChannel,
  getComposeableChannelIds,
  getEditorMode,
  getEmailChannels,
  getSendableMessageChannelIds,
  getSendableMessageChannels,
  getSmsChannels,
  getSubject,
  shouldAssign,
  state,
};
